'use strict';

import extend from 'lodash/extend';

/**
 * Low-level communication handler for the Search service.
 * Uses Q-based promises for all operations.
 */

(function($) {
  window.atex = window.atex || {};
  window.atex.aceui = window.atex.aceui || {};
  window.atex.aceui.service = window.atex.aceui.service || {};

  /**
   * @typedef {Object} SearchRequestOptions - Options and parameters to use when searching.
   * @property {string} collection - The Solr collection against which to perform the search.
   * @property {string} variant - The variant to use when returning content data as part of the search result.
   * @property {string} view - A name of a view to search from.
   * @property {string} authToken - The authentication token used when searching
   * @property {Object} params - The parameters added as query parameters to the ACE REST API search request.
   *   Custom Parameters with multiple values should be declared as Arrays. For example, to send a a parameter
   *   'facet.field' with the values 'text' and 'modifiedBy' it should be declared as
   *   'facet.field': ['text', 'modifiedBy'] which will result in the query string
   *   'face.field=text&facet.field=modifiedBy'
   **/

   /**
   * @param {string} searchServiceUrl - The URL to the search service in the ACE REST API.
   * @param {Object} serviceSettings - An object containing settings for this service.
   * @param {Object} [serviceSettings.customQ = window.Q] - A Q implementations i.e. angular's $q
   * @param {SearchRequestObject} serviceSettings.defaultValues - Default values to use when searching.
   *   The values will override hardcoded defaults and are overridden by any existing individual search requests.
   *   The override is done shallowly per attribute. Unless overridden elsewhere the SearchService will use the
   *   following 'hard coded' default values in the SearchRequestObject:
   *
   *   @example
   *   {
   *     collection: 'internal',
   *     params: {
   *       rows: '100'
   *     }
   *   }
   *
   * @returns {Object} - A new SearchService object
   */
  atex.aceui.service.SearchService = function(searchServiceUrl, serviceSettings) {
    var settings = extend({}, serviceSettings);
    var q = settings.customQ || window.Q;

    var configuredDefaultValues = settings.defaultValues || {};

    var hardDefaultParams = {
      rows: '100'
    };

    var requiredParams = {
      wt: 'json'
    };

    return {
      /**
       * Performs a Solr search through the Content REST API.
       *
       * Argument 'searchRequest' - See 'searchRequestObject' above.
       *
       * Will automatically filter out content in a hidden state unless explicitly
       * included in the fq request parameter.
       *
       * Returns a Q-based promise.
       *
       * If the operation succeeds the returned promise is resolved with an object like:
       *
       * {
       *   data: {
       *     response: {
       *       docs: [{
       *         commitId: 111111,
       *         contentId: 1.100,
       *         eventId: 1000,
       *         modifiedData: '2011-11-11T11:11:11Z',
       *
       *         _data: {
       *           // data shuttle from Polopoly here
       *         }
       *       }],
       *
       *       numFound: 1,
       *       start: 0
       *     },
       *
       *     responseHeader: {
       *       QTime: 1,
       *
       *       params: {
       *         fq: 'page:2.111',
       *         q: '*:*',
       *         rows: 100,
       *         version: "2.2"
       *       }
       *     }
       *   }
       * }
       *
       * Property 'data' is the Solr response data from the search.
       *
       * Rejects the returned promise with an atex.aceui.error.Error object if the
       * operation fails.
       */
      search: function (searchRequest) {
        let deferred = q.defer();
        const SOLR_CONTENTSTATE_HIDDEN = 'ace_contentstate_hidden_b';

        searchRequest.params = extend({}, hardDefaultParams, configuredDefaultValues.params, searchRequest.params, requiredParams);

        searchRequest.params.fq = searchRequest.params.fq || [];

        if (typeof searchRequest.params.fq === 'string') {
          searchRequest.params.fq = [ searchRequest.params.fq ];
        }

        if (!searchRequest.params.fq.find((item) => item.includes(`${SOLR_CONTENTSTATE_HIDDEN}:`))) {
          searchRequest.params.fq.push(`-${SOLR_CONTENTSTATE_HIDDEN}:true`);
        }

        searchRequest.params.variant = searchRequest.params.variant || searchRequest.variant || configuredDefaultValues.variant;
        searchRequest.params.view = searchRequest.params.view || searchRequest.view || configuredDefaultValues.view;
        searchRequest.collection = searchRequest.collection || configuredDefaultValues.collection || 'internal';
        searchRequest.params.inlineData = true;

        var fullUrl = searchServiceUrl + '/' + searchRequest.collection + '/select';

        $.ajax(fullUrl, {
          traditional: true,
          data: searchRequest.params,
          dataType: 'json',

          beforeSend: function (jqXHR) {
            if (searchRequest.authToken !== undefined) {
              jqXHR.setRequestHeader("X-Auth-Token", searchRequest.authToken);
            }
          },

          success: function (data, textStatus, jqXHR) {
            deferred.resolve(data);
          },

          error: function (jqXHR, textStatus, errorThrown) {
            var e = atex.aceui.HTTPUtils.createError(jqXHR, 'atex.aceui.service.SearchService', {
              401: 'Error while searching: User was not correctly authenticated!',
              403: 'Error while searching: Permission denied.',

              'default': 'Unknown error while searching through the Content REST API!'
            });

            if (typeof e.rootCause !== 'undefined') {
              e.rootCause.textStatus = textStatus;
              e.rootCause.errorThrown = errorThrown;

              e.rootCause.searchInfo = searchRequest;
            }

            deferred.reject(e);
          }
        });

        return deferred.promise;
      }
    };
  };
})(jQuery);
